import { FloatingFormItem } from '@/components/_common';
import {
  useCreateRecommendation,
  useRecommendationDetail,
  useUpdateRecommendation,
} from '@/core/domain/people/Recommendation/Recommendation.hooks';
import { RecommendationFormValues } from '@/core/domain/people/Recommendation/Recommendation.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import { Col, Form, Input, Modal, ModalProps, Row } from 'antd';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

interface Props extends Omit<ModalProps, 'onCancel'> {
  onCancel: VoidFunction;
  recommendationId?: number;
}

export default function RecommendationModal({
  recommendationId,
  onCancel,
  ...props
}: Props) {
  const intl = useIntl();
  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const [form] = Form.useForm<RecommendationFormValues>();

  const createMutation = useCreateRecommendation();
  const updateMutation = useUpdateRecommendation();

  const { data, dataUpdatedAt } = useRecommendationDetail(recommendationId);

  useEffect(() => {
    if (data) {
      form.setFieldsValue({ description: data.description });
    }
  }, [dataUpdatedAt, props.open]);

  function handleCancel() {
    form.resetFields();
    onCancel();
  }

  const handleSubmit = async () => {
    const values = await form.validateFields();

    if (recommendationId) {
      await updateMutation.mutateAsync({
        ...values,
        active: true,
        id: recommendationId,
      });
    } else {
      await createMutation.mutateAsync({ active: true, ...values });
    }

    handleCancel();
  };

  return (
    <Modal
      {...props}
      title={`${recommendationId ? 'Editar' : 'Criar'} recomendação`}
      onCancel={handleCancel}
      confirmLoading={updateMutation.isLoading || createMutation.isLoading}
      width={700}
      destroyOnClose
      onOk={handleSubmit}
      okText={recommendationId ? 'Editar' : 'Criar'}
    >
      <Form form={form}>
        <Row gutter={[16, 12]}>
          <Col span={24}>
            <FloatingFormItem
              name="description"
              label={intl.formatMessage({
                id: 'customers.form.recommendation.description',
              })}
              rules={[defaultEmptyFieldError]}
            >
              <Input maxLength={50} />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
